import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { MatchingWeekCategory } from '../db/category.js';
import { Company } from '../db/company.js';
import { MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication, JobApplicationStatus } from '../db/job-application.js';
import { JobPosition } from '../db/job-position.js';
import { MatchingScoreCache } from '../db/MatchingScoreCache.js';
import { MWUserData } from '../db/user.js';
import { assertCallerIsAdmin } from './admin-guard.js';

// ACCEPTED = invited to an interview, BOOKED = invited and slot already booked
const INVITED_STATUSES = [JobApplicationStatus.ACCEPTED, JobApplicationStatus.BOOKED];

@Entity()
export class MatchingWeekStatistics extends AdornisEntity {
  static override _class = 'MatchingWeekStatistics';

  constructor(doc: PartialEntityData<MatchingWeekStatistics>) {
    super(doc);
  }

  @Field(() => Int)
  activeJobPositions: Maybe<number>;

  @Field(() => Int)
  companiesWithActiveJobPositions: Maybe<number>;

  @Field(() => Int)
  newUsers: Maybe<number>;

  @Field(() => Int)
  applications: Maybe<number>;

  @Field(() => Int)
  interviewInvitations: Maybe<number>;

  // "vereinbart": a slot was booked (status BOOKED) — kept separate from interviews actually held
  @Field(() => Int)
  interviewsAgreed: Maybe<number>;

  // "stattgefunden": the company confirmed the interview took place (interviewAttended === true)
  @Field(() => Int)
  interviewsHeld: Maybe<number>;

  // the company confirmed the interview did NOT take place (interviewAttended === false)
  @Field(() => Int)
  interviewsNotAttended: Maybe<number>;
}

export const getMatchingWeekStatistics = registerQuery({
  operationName: 'getMatchingWeekStatistics',
  type: () => MatchingWeekStatistics,
  params: [
    { name: 'from', type: () => Date },
    { name: 'to', type: () => Date },
  ],
  resolve: (from: Date, to: Date) => async (_gqlFields: BaseQLSelectionSet<MatchingWeekStatistics>) => {
    await assertCallerIsAdmin();

    const range = { $gte: from, $lte: to };

    const jobPositionCol = mongo().collection(JobPosition._collectionName);
    // JobPosition has no creation timestamp, so "active in the period" is anchored on the
    // domain dates: the matching day of the job's category or one of its interview slots
    // lies in the period. Jobs of deactivated companies are excluded ($ne: false convention).
    const activeJobs = await jobPositionCol
      .aggregate([
        { $lookup: { from: Company._collectionName, localField: 'companyID', foreignField: '_id', as: 'company' } },
        { $addFields: { company: { $arrayElemAt: ['$company', 0] } } },
        { $match: { 'company.isActive': { $ne: false } } },
        {
          $lookup: {
            from: MatchingWeekCategory._collectionName,
            localField: 'categoryID',
            foreignField: '_id',
            as: 'category',
          },
        },
        { $addFields: { category: { $arrayElemAt: ['$category', 0] } } },
        { $match: { $or: [{ 'category.fixedEventDate': range }, { timeSlots: { $elemMatch: { start: range } } }] } },
        { $group: { _id: null, jobCount: { $sum: 1 }, companyIDs: { $addToSet: '$companyID' } } },
      ])
      .toArray();

    const companyCol = mongo().collection(Company._collectionName);
    const companyUserIDs = await companyCol.distinct('userIDs');

    const userCol = mongo().collection(User._collectionName);
    const jobApplicationCol = mongo().collection(JobApplication._collectionName);
    const [newUserRows, applications, interviewInvitations, interviewsAgreed, interviewsHeld, interviewsNotAttended] =
      await Promise.all([
        // Candidate registrations: company accounts and admins are created by admins, not registered.
        // The user collection is shared with Buchungsheld (same database in production), so only
        // users that went through the matching-week onboarding (mw-user-data exists) are counted.
        userCol
          .aggregate([
            {
              $match: {
                createdAt: range,
                _id: { $nin: companyUserIDs },
                'roles.name': { $ne: MatchingWeekRole.ADMIN },
              },
            },
            { $lookup: { from: MWUserData._collectionName, localField: '_id', foreignField: 'userID', as: 'mw' } },
            { $match: { 'mw.0': { $exists: true } } },
            { $count: 'count' },
          ])
          .toArray(),
        jobApplicationCol.countDocuments({ createdAt: range }),
        jobApplicationCol.countDocuments({ createdAt: range, status: { $in: INVITED_STATUSES } }),
        // "vereinbart": a slot is booked for the period
        jobApplicationCol.countDocuments({ status: JobApplicationStatus.BOOKED, interviewStart: range }),
        // "stattgefunden" / "nicht wahrgenommen": confirmed attendance outcome for the period
        jobApplicationCol.countDocuments({ interviewAttended: true, interviewStart: range }),
        jobApplicationCol.countDocuments({ interviewAttended: false, interviewStart: range }),
      ]);

    return new MatchingWeekStatistics({
      activeJobPositions: activeJobs[0]?.jobCount ?? 0,
      companiesWithActiveJobPositions: activeJobs[0]?.companyIDs?.length ?? 0,
      newUsers: (newUserRows[0]?.count as number | undefined) ?? 0,
      applications,
      interviewInvitations,
      interviewsAgreed,
      interviewsHeld,
      interviewsNotAttended,
    });
  },
});

@Entity()
export class JobPositionApplicationStats extends AdornisEntity {
  static override _class = 'JobPositionApplicationStats';

  // required for client-side construction of entity arrays (see TextMultipleLangItem)
  static override resolveID() {
    return A.getGloballyUniqueID();
  }

  constructor(doc: PartialEntityData<JobPositionApplicationStats>) {
    super(doc);
  }

  @Field(() => ID)
  jobPositionID: Maybe<string>;

  @Field(() => ID)
  companyID: Maybe<string>;

  @Field(() => String)
  jobTitle: Maybe<string>;

  @Field(() => String)
  companyName: Maybe<string>;

  @Field(() => Int)
  applications: Maybe<number>;

  @Field(() => Int)
  invitations: Maybe<number>;

  // "vereinbart": booked slots
  @Field(() => Int)
  interviewsAgreed: Maybe<number>;

  // "stattgefunden": confirmed attended
  @Field(() => Int)
  interviewsHeld: Maybe<number>;
}

export const getMatchingWeekJobApplicationStats = registerQuery({
  operationName: 'getMatchingWeekJobApplicationStats',
  type: () => [JobPositionApplicationStats],
  params: [
    { name: 'from', type: () => Date },
    { name: 'to', type: () => Date },
  ],
  resolve: (from: Date, to: Date) => async (_gqlFields: BaseQLSelectionSet<JobPositionApplicationStats>) => {
    await assertCallerIsAdmin();

    // Applications/invitations are anchored on the application date; agreed/held interviews on the
    // interview date — matching getMatchingWeekStatistics. So all of a job's applications are pulled
    // and each metric filters by its own condition.
    const inCreatedRange = { $and: [{ $gte: ['$$this.createdAt', from] }, { $lte: ['$$this.createdAt', to] }] };
    const inInterviewRange = {
      $and: [
        { $ne: ['$$this.interviewStart', null] },
        { $gte: ['$$this.interviewStart', from] },
        { $lte: ['$$this.interviewStart', to] },
      ],
    };

    // Anchored on job positions (not applications) so positions without any application show up as 0-rows.
    const jobPositionCol = mongo().collection(JobPosition._collectionName);
    const rows = await jobPositionCol
      .aggregate([
        { $lookup: { from: Company._collectionName, localField: 'companyID', foreignField: '_id', as: 'company' } },
        { $addFields: { company: { $arrayElemAt: ['$company', 0] } } },
        { $match: { 'company.isActive': { $ne: false } } },
        {
          $lookup: {
            from: JobApplication._collectionName,
            let: { jobId: '$_id' },
            pipeline: [{ $match: { $expr: { $eq: ['$jobPositionId', '$$jobId'] } } }],
            as: 'apps',
          },
        },
        {
          $project: {
            _id: 0,
            jobPositionID: '$_id',
            companyID: '$companyID',
            jobTitle: '$title',
            companyName: '$company.name',
            applications: { $size: { $filter: { input: '$apps', cond: inCreatedRange } } },
            invitations: {
              $size: {
                $filter: {
                  input: '$apps',
                  cond: { $and: [inCreatedRange, { $in: ['$$this.status', INVITED_STATUSES] }] },
                },
              },
            },
            interviewsAgreed: {
              $size: {
                $filter: {
                  input: '$apps',
                  cond: { $and: [{ $eq: ['$$this.status', JobApplicationStatus.BOOKED] }, inInterviewRange] },
                },
              },
            },
            interviewsHeld: {
              $size: {
                $filter: {
                  input: '$apps',
                  cond: { $and: [{ $eq: ['$$this.interviewAttended', true] }, inInterviewRange] },
                },
              },
            },
          },
        },
        { $sort: { applications: -1, jobTitle: 1 } },
      ])
      .toArray();

    return rows.map(row => new JobPositionApplicationStats(row));
  },
});

@Entity()
export class InterviewInvitationRow extends AdornisEntity {
  static override _class = 'InterviewInvitationRow';

  // required for client-side construction of entity arrays (see TextMultipleLangItem)
  static override resolveID() {
    return A.getGloballyUniqueID();
  }

  constructor(doc: PartialEntityData<InterviewInvitationRow>) {
    super(doc);
  }

  @Field(() => ID)
  applicationID: Maybe<string>;

  @Field(() => ID)
  userID: Maybe<string>;

  @Field(() => ID)
  jobPositionID: Maybe<string>;

  @Field(() => ID)
  companyID: Maybe<string>;

  @Field(() => String)
  firstName: Maybe<string>;

  @Field(() => String)
  lastName: Maybe<string>;

  @Field(() => String)
  email: Maybe<string>;

  @Field(() => String)
  jobTitle: Maybe<string>;

  @Field(() => String)
  companyName: Maybe<string>;

  @Field(() => String)
  status: Maybe<string>;

  @Field(() => Date)
  interviewStart: Maybe<Date>;

  // null = not yet confirmed, true = attended, false = did not take place
  @Field(() => Boolean)
  interviewAttended: Maybe<boolean>;

  @Field(() => Int)
  matchingScore: Maybe<number>;
}

export const getInterviewInvitationList = registerQuery({
  operationName: 'getInterviewInvitationList',
  type: () => [InterviewInvitationRow],
  params: [
    { name: 'from', type: () => Date },
    { name: 'to', type: () => Date },
  ],
  resolve: (from: Date, to: Date) => async (_gqlFields: BaseQLSelectionSet<InterviewInvitationRow>) => {
    await assertCallerIsAdmin();

    const jobApplicationCol = mongo().collection(JobApplication._collectionName);
    const rows = await jobApplicationCol
      .aggregate([
        { $match: { createdAt: { $gte: from, $lte: to }, status: { $in: INVITED_STATUSES } } },
        { $lookup: { from: User._collectionName, localField: 'userId', foreignField: '_id', as: 'user' } },
        { $addFields: { user: { $arrayElemAt: ['$user', 0] } } },
        {
          $lookup: {
            from: JobPosition._collectionName,
            localField: 'jobPositionId',
            foreignField: '_id',
            as: 'jobPosition',
          },
        },
        { $addFields: { jobPosition: { $arrayElemAt: ['$jobPosition', 0] } } },
        {
          $lookup: {
            from: Company._collectionName,
            localField: 'jobPosition.companyID',
            foreignField: '_id',
            as: 'company',
          },
        },
        { $addFields: { company: { $arrayElemAt: ['$company', 0] } } },
        {
          $lookup: {
            from: MatchingScoreCache._collectionName,
            let: { uid: '$userId', jid: '$jobPositionId' },
            pipeline: [
              { $match: { $expr: { $and: [{ $eq: ['$userID', '$$uid'] }, { $eq: ['$jobPositionID', '$$jid'] }] } } },
              { $limit: 1 },
            ],
            as: 'score',
          },
        },
        { $addFields: { score: { $arrayElemAt: ['$score', 0] } } },
        {
          $project: {
            _id: 0,
            applicationID: '$_id',
            userID: '$userId',
            jobPositionID: '$jobPositionId',
            companyID: '$jobPosition.companyID',
            firstName: '$user.firstName',
            lastName: '$user.lastName',
            email: '$user.email',
            jobTitle: '$jobPosition.title',
            companyName: '$company.name',
            status: 1,
            interviewStart: 1,
            interviewAttended: 1,
            matchingScore: '$score.matchingScore.total',
          },
        },
        { $sort: { lastName: 1, firstName: 1, companyName: 1 } },
      ])
      .toArray();

    return rows.map(row => new InterviewInvitationRow(row));
  },
});

@Entity()
export class MatchingScoreBucket extends AdornisEntity {
  static override _class = 'MatchingScoreBucket';

  // required for client-side construction of entity arrays (see TextMultipleLangItem)
  static override resolveID() {
    return A.getGloballyUniqueID();
  }

  constructor(doc: PartialEntityData<MatchingScoreBucket>) {
    super(doc);
  }

  @Field(() => String)
  label: Maybe<string>;

  @Field(() => Int)
  applications: Maybe<number>;

  @Field(() => Int)
  invitations: Maybe<number>;
}

@Entity()
export class MatchingScoreCorrelation extends AdornisEntity {
  static override _class = 'MatchingScoreCorrelation';

  constructor(doc: PartialEntityData<MatchingScoreCorrelation>) {
    super(doc);
  }

  @Field(() => Int)
  scoredApplications: Maybe<number>;

  @Field(() => Int)
  unscoredApplications: Maybe<number>;

  @Field(() => Float)
  avgScoreInvited: Maybe<number>;

  @Field(() => Float)
  avgScoreNotInvited: Maybe<number>;

  // Point-biserial correlation between matching score and "was invited" (-1..1), null if not computable
  @Field(() => Float)
  correlation: Maybe<number>;

  @Field(() => [MatchingScoreBucket])
  buckets: Maybe<MatchingScoreBucket[]>;
}

export const getMatchingScoreCorrelation = registerQuery({
  operationName: 'getMatchingScoreCorrelation',
  type: () => MatchingScoreCorrelation,
  params: [
    { name: 'from', type: () => Date },
    { name: 'to', type: () => Date },
  ],
  resolve: (from: Date, to: Date) => async (_gqlFields: BaseQLSelectionSet<MatchingScoreCorrelation>) => {
    await assertCallerIsAdmin();

    const jobApplicationCol = mongo().collection(JobApplication._collectionName);
    // Only cached scores are used; scores are never calculated here (that would trigger LLM calls).
    const rows = (await jobApplicationCol
      .aggregate([
        { $match: { createdAt: { $gte: from, $lte: to } } },
        {
          $lookup: {
            from: MatchingScoreCache._collectionName,
            let: { uid: '$userId', jid: '$jobPositionId' },
            pipeline: [
              { $match: { $expr: { $and: [{ $eq: ['$userID', '$$uid'] }, { $eq: ['$jobPositionID', '$$jid'] }] } } },
              { $limit: 1 },
            ],
            as: 'score',
          },
        },
        { $addFields: { score: { $arrayElemAt: ['$score.matchingScore.total', 0] } } },
        { $project: { _id: 0, score: 1, invited: { $in: ['$status', INVITED_STATUSES] } } },
      ])
      .toArray()) as Array<{ score?: number | null; invited: boolean }>;

    const scored = rows.filter(r => typeof r.score === 'number');
    const invitedScores = scored.filter(r => r.invited).map(r => r.score!);
    const notInvitedScores = scored.filter(r => !r.invited).map(r => r.score!);

    const mean = (values: number[]) => (values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null);
    const avgScoreInvited = mean(invitedScores);
    const avgScoreNotInvited = mean(notInvitedScores);

    // Point-biserial correlation: r = (M1 - M0) / s * sqrt(p * q)
    let correlation: number | null = null;
    if (invitedScores.length && notInvitedScores.length) {
      const allScores = scored.map(r => r.score!);
      const meanAll = mean(allScores)!;
      const stdDev = Math.sqrt(allScores.reduce((sum, v) => sum + (v - meanAll) ** 2, 0) / allScores.length);
      if (stdDev > 0) {
        const p = invitedScores.length / allScores.length;
        correlation = ((avgScoreInvited! - avgScoreNotInvited!) / stdDev) * Math.sqrt(p * (1 - p));
      }
    }

    // Real-world scores cluster in the 70-100 range, so the upper range is split finer
    // to make the invitation-rate gradient visible.
    const bucketRanges = [
      { label: '0–49', min: 0, max: 49 },
      { label: '50–59', min: 50, max: 59 },
      { label: '60–69', min: 60, max: 69 },
      { label: '70–79', min: 70, max: 79 },
      { label: '80–89', min: 80, max: 89 },
      { label: '90–100', min: 90, max: 100 },
    ];
    const buckets = bucketRanges.map(({ label, min, max }) => {
      const inBucket = scored.filter(r => r.score! >= min && r.score! <= max);
      return new MatchingScoreBucket({
        label,
        applications: inBucket.length,
        invitations: inBucket.filter(r => r.invited).length,
      });
    });

    return new MatchingScoreCorrelation({
      scoredApplications: scored.length,
      unscoredApplications: rows.length - scored.length,
      avgScoreInvited,
      avgScoreNotInvited,
      correlation,
      buckets,
    });
  },
});
